import { useState } from "react";
import { HiChevronDown } from "react-icons/hi2";
import thrivesImage from "../../assets/homethrives3.png";

const accordionItems = [
  {
    id: "athletes",
    title: "Athletes, Artists & Performers",
    content: (
      <p>
        Don&apos;t let rigid school hours kill your talent. Whether you are
        training for Nationals or acting in a film, our &apos;Personalised&apos;
        classes let you study whenever you are free. No attendance pressure,
        tailored to your schedule.
      </p>
    ),
  },
  {
    id: "jee-neet",
    title: "JEE, NEET & CA Aspirants",
    content: (
      <p>
        Stop balancing school attendance with entrance coaching. Save 6-8 hours
        every day. Focus entirely on your competitive exam preparation while we
        handle your school academics.
      </p>
    ),
  },
  {
    id: "unique-needs",
    title: "Students With Unique Learning Needs",
    content: (
      <p>
        Traditional classrooms can be overwhelming and rigid. We provide a
        stress-free environment for students who learn differently (including
        Dyslexia, ADHD, or slow learners). Our &apos;North Star&apos; approach
        adapts to their pace, offering extra time and personalised support to
        ensure they pass with confidence.
      </p>
    ),
  },
  {
    id: "homeschoolers",
    title: "Homeschoolers",
    content: (
      <p>
        Get the best of both worlds: The freedom of homeschooling with the
        safety of a fully recognised Board Certificate. Perfect for families who
        travel or prefer learning from home.
      </p>
    ),
  },
  {
    id: "restart",
    title: "Restarting Education / Gap Years / Second Chance",
    content: (
      <>
        <p>
          A rigid exam schedule shouldn&apos;t define your future. Whether you
          faced unexpected challenges or simply didn&apos;t get the results you
          wanted in IB, CBSE, or Cambridge, you don&apos;t have to repeat the
          entire year.
        </p>
        <p className="mt-3">
          <strong>At Altus,</strong> We offer a streamlined pathway to clear
          your Board Exams without the wait. Secure a fully government-recognised
          mark sheet and move forward to university admissions without losing
          precious time.
        </p>
      </>
    ),
  },
  {
    id: "subject-change",
    title: "Strategic Subject Change & Credit Transfer",
    content: (
      <>
        <p>
          Realized halfway through the year that a subject isn&apos;t helping
          your score? Or did your target university change its requirements?
          Traditional schools lock you in, forcing you to study subjects you no
          longer need.
        </p>
        <p className="mt-3">
          <strong>At Altus -</strong> Switch, drop, or add subjects anytime
          during the year to align with your Undergrad course. We also offer{" "}
          <strong>Transfer of Credits (TOC)</strong>, so you can carry forward
          good marks from your previous board and only sit for the subjects you
          need to fix.
        </p>
      </>
    ),
  },
  {
    id: "hybrid",
    title: "Hybrid Learning",
    content: (
      <p>
        Online learning shouldn&apos;t mean isolation. We encourage a hybrid
        lifestyle where students master academics online but pursue sports, arts,
        and social projects in the real world.
      </p>
    ),
  },
];

function HomeWhoThrives() {
  const [openId, setOpenId] = useState(accordionItems[0].id);

  return (
    <section
      className="relative overflow-hidden bg-[#fbfbfd] py-16 sm:py-20 lg:py-24"
      aria-labelledby="home-who-thrives-heading"
    >
      <div className="relative mx-auto max-w-7xl px-4 sm:px-8 md:px-10 lg:px-8">
        <div className="grid gap-10 lg:grid-cols-2 lg:items-stretch lg:gap-14 xl:gap-16">
          <div className="relative min-h-0 h-72 w-full sm:h-80 lg:h-full">
            <div className="relative h-full min-h-[16rem] overflow-hidden rounded-2xl border border-brand/10 bg-brand-light/30 shadow-[0_18px_44px_-16px_rgba(30,22,51,0.14)] ring-1 ring-brand/5 lg:min-h-0">
              <img
                src={thrivesImage}
                alt="Teacher guiding students in a bright classroom with a Back to School whiteboard"
                className="absolute inset-0 h-full w-full object-cover object-center"
                width={800}
                height={1000}
                loading="lazy"
              />
            </div>
          </div>

          <div className="flex min-h-0 flex-col lg:min-h-full">
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-brand sm:text-[0.8125rem]">
              Who we serve
            </p>
            <h2
              id="home-who-thrives-heading"
              className="mt-4 text-balance text-3xl font-bold tracking-tight text-ink sm:text-4xl md:text-[2.375rem] md:leading-tight"
            >
              WHO THRIVES AT ALTUS?
            </h2>

            <div
              className="mt-8 divide-y divide-brand/10 rounded-2xl border border-brand/10 bg-white/80 shadow-sm ring-1 ring-brand/5"
              role="region"
              aria-label="Who thrives at Altus"
            >
              {accordionItems.map((item) => {
                const isOpen = openId === item.id;
                const panelId = `who-thrives-panel-${item.id}`;
                const buttonId = `who-thrives-trigger-${item.id}`;

                return (
                  <div key={item.id} className="first:rounded-t-2xl last:rounded-b-2xl">
                    <h3 className="m-0 text-base font-bold leading-snug text-ink">
                      <button
                        id={buttonId}
                        type="button"
                        className="flex w-full items-center justify-between gap-3 px-4 py-4 text-left transition hover:bg-brand-light/40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-brand sm:px-5 sm:py-4"
                        aria-expanded={isOpen}
                        aria-controls={panelId}
                        onClick={() =>
                          setOpenId((prev) =>
                            prev === item.id ? null : item.id
                          )
                        }
                      >
                        <span className="min-w-0 flex-1">{item.title}</span>
                        <HiChevronDown
                          className={`h-5 w-5 shrink-0 text-brand transition-transform duration-200 ${
                            isOpen ? "rotate-180" : ""
                          }`}
                          aria-hidden
                        />
                      </button>
                    </h3>
                    <div
                      id={panelId}
                      role="region"
                      aria-labelledby={buttonId}
                      className={isOpen ? "block" : "hidden"}
                    >
                      <div className="border-t border-brand/5 px-4 pb-4 pt-0 text-pretty text-sm leading-relaxed text-ink/75 sm:px-5 sm:pb-5 sm:text-[0.9375rem]">
                        {item.content}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default HomeWhoThrives;
